import { useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import useTeamMemberStore from '@/stores/TeamMemberStore';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { updateTeamMemberRole } from '@/hooks/agent';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ButtonLoading } from '@/components/common/button/loading-button';

const roles = ['agent', 'admin', 'opener', 'closer'];

const ChangeRoleDialog = ({ isOpen, onOpenChange, agent }) => {
  const availableRoles = agent ? roles.filter((r) => r !== agent.role) : [];
  const [role, setRole] = useState(availableRoles[0]);
  const [isLoading, setIsLoading] = useState(false);

  const { updateAgentRole } = useTeamMemberStore(
    useShallow((state) => ({ updateAgentRole: state.updateAgentRole }))
  );

  const { updateOwnerRole } = useInboxStore(
    useShallow((state) => ({ updateOwnerRole: state.updateOwnerRole }))
  );

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!agent) return;

    try {
      setIsLoading(true);
      await updateTeamMemberRole(agent._id, role);
      updateAgentRole(agent._id, role);
      updateOwnerRole(agent._id, role);
      toast.success('Role updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update role');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {agent && (
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update role for {agent.first_name} {agent.last_name}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-3'>
            <div className='text-sm font-medium'>Select role</div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue placeholder='Select a role' />
              </SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                <SelectGroup>
                  {availableRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className='mt-5'>
            <div className='flex w-full flex-row items-center justify-end'>
              <div className='flex space-x-2'>
                <Button variant='secondary' onClick={handleClose}>
                  Cancel
                </Button>
                {isLoading ? (
                  <ButtonLoading />
                ) : (
                  <Button onClick={handleSave}>Save</Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ChangeRoleDialog;
